/**
 * Merch planner — el simulador de hora punta, puro (sin React, sin API).
 *
 * Reproduce un día de evento minuto a minuto: llegan jugadores, hacen cola en la estación más corta,
 * juegan, ganan y se van. La animación ilustra; el NÚMERO del veredicto lo da el motor (`rushVerdict`,
 * D-8), nunca la cola simulada, para que no dependa del redondeo de la animación.
 *
 * Curva de llegada con media 1 (D-7):
 *   steady  f(x) = 1                         F(x) = x
 *   rush    f(x) = 0,5 + (π/4)·sen(πx)       F(x) = 0,5x + (1 − cos πx)/4      pico = 0,5 + π/4 ≈ 1,285 a media jornada
 * Las llegadas del minuto t salen de la acumulada, así el día suma exactamente `players`.
 *
 * Nota: `Throughput.peakPlayersPerHour` del motor es la MEDIA por hora (como `giveaway.ts:254-282`
 * de la web); aquí se multiplica por `RUSH_PEAK` para el pico real.
 */
import { ceilSafe, type Throughput } from './engine';

export type Crowd = 'steady' | 'rush';

/** Pico de la curva de llegada con pico, relativo a la media. */
export const RUSH_PEAK = 0.5 + Math.PI / 4;

/** Densidad relativa de llegadas en x ∈ [0,1] (media 1). */
export function arrivalRate(crowd: Crowd, x: number): number {
  if (crowd === 'steady') return 1;
  const c = Math.min(1, Math.max(0, x));
  return 0.5 + (Math.PI / 4) * Math.sin(Math.PI * c);
}

/** Acumulada: F(0) = 0, F(1) = 1, no decreciente. */
export function arrivalShare(crowd: Crowd, x: number): number {
  const c = Math.min(1, Math.max(0, x));
  if (crowd === 'steady') return c;
  return 0.5 * c + (1 - Math.cos(Math.PI * c)) / 4;
}

export interface SimInput {
  players: number;
  dayMinutes: number;
  minutesPerHandout: number;
  stations: number;
  crowd: Crowd;
}

export interface SimState {
  /** Minutos transcurridos, 0…dayMinutes. */
  t: number;
  /** Personas en cada estación, incluida la que juega. */
  queues: number[];
  progress: number[];
  served: number[];
  /** Llegadas acumuladas. */
  arrived: number;
  /** Máximo de una cola a final de minuto en todo el día. */
  longestLine: number;
}

const EPS = 1e-9;

function check(input: SimInput): void {
  const { players, dayMinutes, minutesPerHandout, stations } = input;
  if (!Number.isInteger(stations) || stations < 1) throw new RangeError('stations must be an integer ≥ 1');
  if (!Number.isInteger(dayMinutes) || dayMinutes < 1) throw new RangeError('dayMinutes must be an integer ≥ 1');
  if (!(minutesPerHandout > 0) || !Number.isFinite(minutesPerHandout)) throw new RangeError('minutesPerHandout must be > 0');
  if (!Number.isInteger(players) || players < 0) throw new RangeError('players must be an integer ≥ 0');
}

export function startDay(input: SimInput): SimState {
  check(input);
  const zeros = () => Array.from({ length: input.stations }, () => 0);
  return { t: 0, queues: zeros(), progress: zeros(), served: zeros(), arrived: 0, longestLine: 0 };
}

/** Un minuto (D-10). En t ≥ dayMinutes devuelve el mismo estado. No muta `state`. */
export function step(input: SimInput, state: SimState): SimState {
  if (state.t >= input.dayMinutes) return state;
  const { players, dayMinutes, minutesPerHandout: m, crowd } = input;
  const queues = state.queues.slice();
  const progress = state.progress.slice();
  const served = state.served.slice();
  const arrivals =
    Math.floor(players * arrivalShare(crowd, (state.t + 1) / dayMinutes) + EPS) -
    Math.floor(players * arrivalShare(crowd, state.t / dayMinutes) + EPS);
  for (let a = 0; a < arrivals; a += 1) {
    let k = 0;
    for (let i = 1; i < queues.length; i += 1) if (queues[i] < queues[k]) k = i;
    queues[k] += 1;
  }
  for (let i = 0; i < queues.length; i += 1) {
    if (queues[i] > 0) {
      progress[i] += 1;
      while (progress[i] >= m && queues[i] > 0) {
        queues[i] -= 1;
        served[i] += 1;
        progress[i] -= m;
      }
      if (queues[i] === 0) progress[i] = 0;
    } else progress[i] = 0;
  }
  return {
    t: state.t + 1,
    queues,
    progress,
    served,
    arrived: state.arrived + Math.max(0, arrivals),
    longestLine: Math.max(state.longestLine, ...queues),
  };
}

/** Hasta el final del día (reduced motion y pruebas). */
export function runDay(input: SimInput): SimState {
  let s = startDay(input);
  while (s.t < input.dayMinutes) s = step(input, s);
  return s;
}

export function waitMinutes(queue: number, minutesPerHandout: number): number {
  return Math.round(queue * minutesPerHandout);
}

export interface Verdict {
  crowd: Crowd;
  peakPerHour: number;
  capacityPerHour: number;
  /** Puntos de entrega (estaciones o staff de campo) que faltan. */
  extra: number;
  covered: boolean;
}

/** D-8. `covered = extra === 0`. Rush nunca pide menos que el motor. */
export function rushVerdict(capacity: Throughput, crowd: Crowd): Verdict {
  const avg = capacity.peakPlayersPerHour;
  let peak = avg;
  let extra = capacity.extraPoints;
  if (crowd === 'rush') {
    peak = ceilSafe(avg * RUSH_PEAK);
    const rushNeeded = capacity.perPointPerHour > 0 ? ceilSafe(peak / capacity.perPointPerHour) : 0;
    extra = Math.max(capacity.extraPoints, rushNeeded - capacity.points, 0);
  }
  return { crowd, peakPerHour: peak, capacityPerHour: capacity.capacityPerHour, extra, covered: extra === 0 };
}
